// src/components/email/SendAcceptanceEmailModal.jsx
import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { X, Send, Mail } from "lucide-react";
import { toast } from "react-toastify";
import {
  DEFAULT_ACCEPTANCE_TEMPLATES,
  ACCEPTANCE_TEMPLATE_META,
} from "../../utils/acceptanceEmails";
import { useStateValue } from "../../context/StateProvider";
import {
  composeAcceptanceEmail,
} from "../../utils/acceptanceEmailComposer";
import {
  buildBanner,
  buildSignature,
  buildFooter,
  wrapEmail,
  paragraphsToHtml,
} from "../../utils/openHouseEmailsHtml";
import { sendOpenHouseEmail } from "../../utils/emailSender";

export default function SendAcceptanceEmailModal({
  deal,
  contacts,
  sender,
  settingsDefaults,
  templateId,
  recipientName,
  recipientEmail,
  mode,
  onClose,
  onSent,
}) {
  const [{ emailTemplates }] = useStateValue();
  const meta = ACCEPTANCE_TEMPLATE_META[templateId];

  // Saved template (user-customized in Settings) or hardcoded default.
  const saved = emailTemplates?.[templateId] || {};
  const base = DEFAULT_ACCEPTANCE_TEMPLATES[templateId];
  const initialSubject = saved.subject ?? base.subject;
  const initialBody = saved.body ?? base.body;

  // Render with the deal context so the editor pre-fills resolved values
  // for the user to tweak. Storing the rendered text means edits remain
  // one-off and the saved template stays untouched.
  const { subject: renderedSubject, text: renderedText } = useMemo(
    () => composeAcceptanceEmail(templateId, deal, contacts, sender, settingsDefaults, {
      subject: initialSubject,
      body: initialBody,
    }),
    [templateId, deal, contacts, sender, settingsDefaults, initialSubject, initialBody],
  );

  const [subjectDraft, setSubjectDraft] = useState(renderedSubject);
  const [bodyDraft, setBodyDraft] = useState(renderedText);
  const [sending, setSending] = useState(false);

  const html = useMemo(() => {
    try {
      const banner = buildBanner({ propertyAddress: deal?.propertyaddress || "" }, "");
      const body = paragraphsToHtml(bodyDraft);
      const signature = buildSignature(sender);
      const footer = buildFooter({});
      return wrapEmail({ banner, body, signature, footer });
    } catch (err) {
      return `<pre>Preview error: ${err.message}</pre>`;
    }
  }, [bodyDraft, deal?.propertyaddress, sender]);

  const previewOnly = mode === "preview";

  const handleSend = async () => {
    if (!recipientEmail) {
      toast.error("No recipient email.");
      return;
    }
    setSending(true);
    try {
      const res = await sendOpenHouseEmail({
        userId: sender?.id,
        to: recipientEmail,
        toName: recipientName,
        subject: subjectDraft,
        text: bodyDraft,
        html,
        type: templateId,
        relatedDealId: deal?.id || deal?.createdat || "",
      });
      if (!res?.ok) {
        toast.error(`Send failed: ${res?.error || "unknown"}`);
        return;
      }
      const record = {
        sentAt: new Date(),
        sentBy: sender?.id || null,
        sentByName: `${sender?.firstname || ""} ${sender?.lastname || ""}`.trim(),
        recipientEmail,
        recipientName: recipientName || "",
        subject: subjectDraft,
        gmailMessageId: res.gmailMessageId || null,
      };
      toast.success("Email Sent");
      onSent?.(record);
    } catch (err) {
      console.error("Acceptance send failed:", err);
      toast.error("Send failed. Check your connection.");
    } finally {
      setSending(false);
    }
  };

  return createPortal(
    <>
      <div
        className="fixed inset-0 bg-black/55 z-[100]"
        onClick={sending ? undefined : onClose}
      />
      <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 pointer-events-none">
        <div className="bg-white rounded-xl w-full max-w-4xl shadow-2xl flex flex-col max-h-[92vh] pointer-events-auto">
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <Mail size={18} className="text-indigo-600" />
              <h2 className="text-base font-semibold text-slate-900">{meta?.label}</h2>
            </div>
            <button
              onClick={onClose}
              disabled={sending}
              className="w-8 h-8 hover:bg-slate-100 rounded-full flex items-center justify-center disabled:opacity-50"
            >
              <X size={16} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-4 grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">To</label>
                <input
                  type="text"
                  value={`${recipientName ? recipientName + " · " : ""}${recipientEmail || ""}`}
                  readOnly
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-slate-50 text-slate-700"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Subject</label>
                <input
                  value={subjectDraft}
                  onChange={(e) => setSubjectDraft(e.target.value)}
                  readOnly={previewOnly}
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Body</label>
                <textarea
                  value={bodyDraft}
                  onChange={(e) => setBodyDraft(e.target.value)}
                  readOnly={previewOnly}
                  rows={18}
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono leading-relaxed"
                />
                <p className="text-[11px] text-slate-400 mt-1">
                  Edits here only affect this one send. To change the default, go to Settings → Email Templates.
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Preview · how the recipient will see this
              </div>
              <div className="border border-slate-200 rounded-md overflow-hidden bg-slate-50">
                <div className="px-3 py-2 border-b border-slate-200 bg-white text-[12px] text-slate-700">
                  <strong>Subject:</strong> {subjectDraft}
                </div>
                <iframe
                  title="acceptance-email-preview"
                  srcDoc={html}
                  className="w-full h-[520px] bg-white"
                />
              </div>
            </div>
          </div>

          <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2 bg-slate-50">
            <button
              onClick={onClose}
              disabled={sending}
              className="px-4 py-2 text-sm font-semibold rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              {previewOnly ? "Close" : "Cancel"}
            </button>
            {!previewOnly && (
              <button
                onClick={handleSend}
                disabled={sending}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50"
              >
                <Send size={14} />
                {sending ? "Sending…" : "Send"}
              </button>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
